import {
    includeCredentials,
    HttpCodes,
    HttpHeaders,
    ContentTypes,
    RequestMethods,
    RequestModeCors,
    hostUrl
} from '../constants';
import {
    getApiError,
    createApiError,
    createException,
    unauthorizedAction,
    tryStringifyByContent,
    stringifyQuery,
    IInputQuery,
} from '../common';
import { logger } from '@telstra-connect/logger';
declare const global;

export type RequestHeaders = HeadersInit;

export interface IBaseRequestParams<T> {
    url: string;
    queryString?: IInputQuery;
    hostUrl?: string;
    headers?: RequestHeaders;
    method: RequestMethods;
    contentType?: ContentTypes;
    body?: T;
    processResponse?: <R>(response: Response) => Promise<R>;
}

export interface IBaseFormDataRequestParams {
    url: string;
    queryString?: IInputQuery;
    hostUrl?: string;
    headers?: RequestHeaders;
    method: RequestMethods;
    body: FormData;
}

const toHeaders = (headers: RequestHeaders): Headers => {
    return new Headers({
        ...(headers || {})
    });
}

const decorateQuery = (url: string, queryString?: IInputQuery) => {
    return queryString ? `${url}?${stringifyQuery(queryString)}` : url;
};

const resolveURL = (params: { hostUrl?: string, url: string }) => {
    if (params.hostUrl) {
        return params.hostUrl;
    }
    const platform = global?.TCONN?.Platform;
    if (platform && typeof platform.resolveTargetURL === 'function') {
        return platform.resolveTargetURL(params.url);
    }
    return hostUrl;
}

const formatUrl = (params: { hostUrl?: string, url: string, queryString?: IInputQuery }) => {
    return `${resolveURL(params)}${decorateQuery(params.url, params.queryString)}`;
}

export const baseRequest = <TResult, TBody = {}>(params: IBaseRequestParams<TBody>) => {
    const { headers: requestHeaders, body: requestBody, contentType, processResponse } = params;

    const headers = toHeaders(requestHeaders)
    const body = tryStringifyByContent(requestBody, contentType);
    const url = formatUrl(params);

    const init = {
        headers,
        method: params.method,
        mode: RequestModeCors,
        body
    };

    const fetchParams = {
        logBodyWhenError: true,
        ...processResponse && { processResponse }
    };

    return fetchIt<TResult>(url, init, fetchParams);
};

export const baseFormDataRequest = <TResult>(params: IBaseFormDataRequestParams) => {
    const headers = toHeaders(params.headers)

    const init = {
        headers,
        method: params.method,
        mode: RequestModeCors,
        body: params.body
    };

    const url = formatUrl(params);
    return fetchIt<TResult>(url, init, { logBodyWhenError: false });
};

interface IFetchItParams {
    logBodyWhenError: boolean;
    processResponse?: <R>(response: Response) => Promise<R>;
}

const fetchIt = <TResult>(url: string, init: RequestInit, params: IFetchItParams = { logBodyWhenError: true }):
Promise<TResult> => {
    const fetchOptions: RequestInit = {
        ...init,
        credentials: includeCredentials === true ? 'include' : 'omit',

        // Don't follow redirects as they could be from a malicious source.
        // If we ever have the need to follow them we should add a whitelist
        // of acceptable hosts, not just follow without any validation.
        redirect: 'error',
    };

    if (typeof url !== 'string') {
        throw new Error('URL is not a string');
    }

    return fetch(url, fetchOptions)
        .then((response) => {
            if (response.status === HttpCodes.Unauthorized) {
                unauthorizedAction();
                throw createException(response);
            }

            if (response.status > HttpCodes.BadRequest) {
                throw createException(response);
            }

            return response;
        })
        .then((response): Promise<TResult> => {
            if (response.status === HttpCodes.BadRequest) {
                return getApiError(response)
                    .then((genericError) => {
                        throw createException(response, genericError);
                    });
            }

            if (params.processResponse) {
                return params.processResponse(response);
            } else {

                const contentType = (response.headers.get(HttpHeaders.ContentType) || '').toLowerCase();

                if (contentType.indexOf(ContentTypes.JSON) !== -1) {
                    return response.json();
                }
            }
        })
        .catch((error) => {
            const exception = createApiError(error, {
                url,
                body: init.body,
                headers: init.headers as Headers,
                method: init.method
            });

            logger().errorAsync({
                errorName: exception.name,
                message: exception.message,
                url,
                ...params.logBodyWhenError ? { body: init.body } : {},
                method: init.method,
                headers: exception.headers,
                statusCode: exception.statusCode,
                data: exception.data,
                stack: exception.stack
            });

            throw error;
        });
};
